import CtaArrow from "@/components/svg/CtaArrow";
import SubHeader from "@/components/SubHeader";
import { isFilled } from "@prismicio/client";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";
import { PrismicRichText } from "@prismicio/react";
import { getSubTitleComponents, getTextComponents, getTitleComponents } from "@/helpers";


/**
 * @param {import("@prismicio/client").Content.ProjectsListSliceDefaultPrimaryProjectsItem} item
 */
const ProjectCard = ({ item, array }) => {
  const { partner, project } = item;

  if (!isFilled.contentRelationship(project)) {
    return null;
  }

  const projectData = project.data;
  const previewImage = projectData?.preview_image;
  const previewTitle = projectData?.preview_title;
  const backgroundImage = isFilled.image(previewImage)
    ? previewImage
    : isFilled.image(partner)
      ? partner
      : null;

  if (!backgroundImage && !isFilled.image(partner) && !isFilled.richText(previewTitle)) {
    return null;
  }

  return (
    <PrismicNextLink prefetch={false}
      field={project}
      className={`relative flex h-[350px] w-full shrink-0 snap-center flex-col justify-start overflow-hidden rounded-[30px] p-5 ${array?.length <= 2 ? "md:h-[366px] lg:h-[500px]" : "md:h-[366px]"} md:flex-1`}
    >
      {isFilled.image(backgroundImage) && (
        <PrismicNextImage
          field={backgroundImage}
          fill
          className="object-cover hover:scale-110 transition-transform duration-500"
          sizes="(max-width: 768px) 100vw, 400px"
        />
      )}

      <div className="relative z-10 flex items-start">
        <div className="inline-flex items-center gap-2.5 rounded-full bg-beige-30 py-1.5 pl-2 pr-3">
          {isFilled.image(partner) && (
            <PrismicNextImage
              field={partner}
              className="size-7 shrink-0 rounded-full object-cover"
              sizes="28px"
            />
          )}

          {isFilled.richText(previewTitle) &&
            <PrismicRichText field={previewTitle} components={getTextComponents({ color: "bleu", variation: "lg", className: "font-bold uppercase" })} />
          }
        </div>
      </div>
    </PrismicNextLink>
  );
};

/**
 * @typedef {import("@prismicio/client").Content.ProjectsListSlice} ProjectsListSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ProjectsListSlice>} ProjectsListProps
 * @type {import("react").FC<ProjectsListProps>}
 */
const ProjectsList = ({ slice }) => {
  const { sub_header, title, sub_title, projects, cta, bg_color } = slice.primary;
  const highlightColor = bg_color === "beige-30" ? "beige" : "beige-30";

  const filledProjects = Array.isArray(projects)
    ? projects.filter((item) => isFilled.contentRelationship(item.project))
    : [];

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`contre-uno bg-${bg_color} padding-x padding-y`}
    >
      <div className="framing-lg flex flex-col gap-10 md:items-end md:gap-15">
        <div className="flex w-full flex-col gap-3">
          {isFilled.keyText(sub_header) && <SubHeader text={sub_header} />}

          {isFilled.richText(title) && (
            <PrismicRichText
              field={title}
              components={getTitleComponents({
                color: "bleu",
                highlightColor,
              })}
            />
          )}

          {isFilled.richText(sub_title) && (
            <PrismicRichText
              field={sub_title}
              components={{
                ...getSubTitleComponents({ color: "bleu" }),
              }}
            />
          )}
        </div>

        {filledProjects.length > 0 && (
          <div className="flex w-full flex-col gap-5 md:flex-row md:snap-x md:snap-mandatory md:overflow-x-auto md:pb-2 [-ms-overflow-style:none] [scrollbar-width:none] md:[&::-webkit-scrollbar]:hidden">
            {filledProjects.map((item, index, array) => (
              <ProjectCard key={`project-${index}`} item={item} array={array} />
            ))}
          </div>
        )}

        {isFilled.link(cta) && (
          <PrismicNextLink prefetch={false}
            field={cta}
            className="cta cta-text flex w-full flex-row gap-5 border-[3px] border-rouge bg-rouge text-beige-30 md:w-auto"
          >
            {cta.text}
            <CtaArrow className="text-beige-30" />
          </PrismicNextLink>
        )}
      </div>
    </section>
  );
};

export default ProjectsList;
